import { useEffect, useRef } from 'react';

interface FloatingShape {
  x: number;
  y: number;
  size: number;
  speedX: number;
  speedY: number;
  opacity: number;
  shape: 'circle' | 'square' | 'hexagon';
  color: string;
}

interface FloatingShapesProps {
  count?: number;
  color?: 'accent' | 'secondary' | 'primary';
  speed?: number;
  className?: string;
}

export default function FloatingShapes({ count = 12, color = 'accent', speed = 1, className = '' }: FloatingShapesProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const shapesRef = useRef<FloatingShape[]>([]);
  const animRef = useRef<number>(0);

  const colorMap = {
    accent: '112, 87, 255',
    secondary: '34, 211, 197',
    primary: '17, 26, 53',
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      const parent = canvas.parentElement;
      if (parent) {
        canvas.width = parent.offsetWidth;
        canvas.height = parent.offsetHeight;
      }
    };

    resize();
    window.addEventListener('resize', resize);

    // Initialize shapes
    const rgb = colorMap[color];
    shapesRef.current = Array.from({ length: count }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      size: Math.random() * 20 + 8,
      speedX: (Math.random() - 0.5) * 0.3 * speed,
      speedY: (Math.random() - 0.5) * 0.3 * speed,
      opacity: Math.random() * 0.15 + 0.03,
      shape: (['circle', 'square', 'hexagon'] as const)[Math.floor(Math.random() * 3)],
      color: rgb,
    }));

    const drawHexagon = (x: number, y: number, size: number) => {
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI / 3) * i - Math.PI / 6;
        const hx = x + size * Math.cos(angle);
        const hy = y + size * Math.sin(angle);
        if (i === 0) ctx.moveTo(hx, hy);
        else ctx.lineTo(hx, hy);
      }
      ctx.closePath();
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      shapesRef.current.forEach((shape) => {
        shape.x += shape.speedX;
        shape.y += shape.speedY;

        // Bounce off edges
        if (shape.x < -shape.size) shape.x = canvas.width + shape.size;
        if (shape.x > canvas.width + shape.size) shape.x = -shape.size;
        if (shape.y < -shape.size) shape.y = canvas.height + shape.size;
        if (shape.y > canvas.height + shape.size) shape.y = -shape.size;

        ctx.fillStyle = `rgba(${shape.color}, ${shape.opacity})`;
        ctx.strokeStyle = `rgba(${shape.color}, ${shape.opacity * 2})`;
        ctx.lineWidth = 1;

        if (shape.shape === 'circle') {
          ctx.beginPath();
          ctx.arc(shape.x, shape.y, shape.size, 0, Math.PI * 2);
          ctx.fill();
          ctx.beginPath();
          ctx.arc(shape.x, shape.y, shape.size + 2, 0, Math.PI * 2);
          ctx.stroke();
        } else if (shape.shape === 'square') {
          const half = shape.size / 2;
          ctx.fillRect(shape.x - half, shape.y - half, shape.size, shape.size);
          ctx.strokeRect(shape.x - half - 2, shape.y - half - 2, shape.size + 4, shape.size + 4);
        } else {
          drawHexagon(shape.x, shape.y, shape.size);
          ctx.fill();
          ctx.beginPath();
          drawHexagon(shape.x, shape.y, shape.size + 3);
          ctx.stroke();
        }
      });

      // Draw connecting lines between nearby shapes
      shapesRef.current.forEach((shape, i) => {
        shapesRef.current.slice(i + 1).forEach((other) => {
          const dx = shape.x - other.x;
          const dy = shape.y - other.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 150) {
            ctx.beginPath();
            ctx.moveTo(shape.x, shape.y);
            ctx.lineTo(other.x, other.y);
            ctx.strokeStyle = `rgba(${shape.color}, ${0.05 * (1 - dist / 150)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        });
      });

      animRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animRef.current);
    };
  }, [count, color, speed]);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 w-full h-full pointer-events-none ${className}`}
    />
  );
}